import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Menu,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Typography,
  Button,
  CssBaseline,
  Snackbar,
  Alert,
  Tooltip,
  Backdrop,
  CircularProgress,
} from '@mui/material';
import { DataGridPro, GridToolbar } from '@mui/x-data-grid-pro';
import { useNavigate, Link, useOutletContext } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import { yellow, brown } from '../../styles/ThemePrimitives';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop';
import * as XLSX from 'xlsx';
import DownloadIcon from '@mui/icons-material/Download';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

import AppTheme from '../../styles/AppTheme';
import CancelReceiptDialog from '../../components/accounts/receipts/CancelReceiptDialog';
import EditReceiptDialog from '../../components/accounts/receipts/EditReceiptDialog';
import RazorpayReconcileDialog from '../../components/accounts/receipts/RazorpayReconcileDialog';

const customLocaleText = {
  noRowsLabel: 'Please wait....',
  noResultsOverlayLabel: '',
};

// --- helper: normalize API status to UI label ---
const toUiStatus = (status) => {
  const s = String(status || '').toLowerCase();
  if (s === 'paid') return 'Deposited';
  if (s === 'pending') return 'Pending';
  if (s === 'cancelled' || s === 'canceled') return 'Cancelled';
  return status || '';
};

function Receipts() {
  const { selectedSector, selectedSubSector, selectedYear, selectedSectorName, selectedSubSectorName } = useOutletContext();
  const [loadingData, setLoadingData] = useState(false);

  // NEW: status filter state (All | Deposited | Pending)
  const [statusFilter, setStatusFilter] = useState('All');

  const [modeFilter, setModeFilter] = useState('All');
   const [createdByFilter, setCreatedByFilter] = useState('');
     const [userNames, setUserNames] = useState([]);

  const { token, loading, accessRoleId } = useUser();
  const [rows, setRows] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [sortModel, setSortModel] = useState([]);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  const navigate = useNavigate();

  // Snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  // Edit dialog state
  const [editOpen, setEditOpen] = useState(false);
  const [editReceipt, setEditReceipt] = useState(null);
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  // Export filtered rows to Excel (Status mapped to UI label)
  const exportToExcel = () => {
    if (!filteredRows.length) {
      alert('No data to export.');
      return;
    }
    const exportData = filteredRows.map((row) => ({
      Name: row.name,
      ITS: row.its,
      Mobile: row.mobile,
      'Folio No': row.folio_no,
      Sector: row.sector_name,
      'Sub Sector': row.sub_sector_name,
      Mode: row.mode,
      Date: row.date,
      Year: row.year,
      Comments: row.comments,
      Amount: row.amount,
      ReceiptNo: row.receipt_no,
      Status: toUiStatus(row.status), // <-- never shows "Paid"; shows "Deposited"
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Receipts');
    XLSX.writeFile(workbook, `Receipts_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  // Button-with-menu inside Action column
  const ActionButtonWithOptions = ({ row, onEdit }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [openCancelDialog, setOpenCancelDialog] = useState(false);
    const open = Boolean(anchorEl);

    const handleClick = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const handlePrintClick = () => {
      const printUrl = `https://api.fmb52.com/api/receipt_print/${row.hashed_id}`;
      window.open(printUrl, '_blank');
      handleClose();
    };

    const handleCancelClick = () => {
      setOpenCancelDialog(true);
      handleClose();
    };

    const handleConfirmCancel = (id) => {
      // TODO: integrate cancel API
      console.log('Cancel confirmed for receipt ID:', id);
    };

    useEffect(() => {
      document.title = 'Receipts - FMB 52';
    }, []);

    return (
      <Box>
        <Button variant="contained" color="primary" onClick={handleClick}>
          Actions
        </Button>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          <MenuItem onClick={handlePrintClick}>
            <Tooltip title="View/Print Receipt" placement="left">
              <Box display="flex" alignItems="center" gap={1} sx={{ pr: 2 }}>
                <LocalPrintshopIcon sx={{ color: brown[200] }} />
                Print
              </Box>
            </Tooltip>
          </MenuItem>

          <MenuItem
            onClick={() => {
              onEdit?.(row);
              handleClose();
            }}
          >
            <Tooltip title="Edit" placement="left">
              <Box display="flex" alignItems="center" gap={1} sx={{ pr: 2 }}>
                <EditIcon sx={{ color: brown[200] }} />
                Edit
              </Box>
            </Tooltip>
          </MenuItem>

          <MenuItem onClick={handleCancelClick}>
            <Tooltip title="Cancel" placement="left">
              <Box display="flex" alignItems="center" gap={1} sx={{ pr: 2 }}>
                <CancelIcon sx={{ color: brown[200] }} />
                Cancel
              </Box>
            </Tooltip>
          </MenuItem>
        </Menu>

        <CancelReceiptDialog
          open={openCancelDialog}
          onClose={() => setOpenCancelDialog(false)}
          receiptId={row.id}
          onConfirm={handleConfirmCancel}
          setSnackbarOpen={setSnackbarOpen}
          setSnackbarMessage={setSnackbarMessage}
          setSnackbarSeverity={setSnackbarSeverity}
        />
      </Box>
    );
  };

  // Columns
  const columns = [
    {
      field: 'mumeneen_info',
      headerName: 'Mumeneen Info',
      width: 570,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '16px',
            }}
          >
            <img
              src={params.row.photo_url ? params.row.photo_url : '/static/images/avatar-placeholder.png'}
              alt="User Photo"
              style={{
                width: 80,
                height: 90,
                border: '2px solid #ddd',
                borderRadius: '8px',
                objectFit: 'cover',
              }}
            />
          </div>

          <Box sx={{ display: 'flex', flexDirection: 'column', paddingTop: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: yellow[300] }}>
              Name:{' '}
              <Link
                to={`/mumeneen/${params.row.family_id}`}
                style={{ color: yellow[400], textDecoration: 'none' }}
                onMouseEnter={(e) => (e.target.style.color = brown[700])}
                onMouseLeave={(e) => (e.target.style.color = yellow[400])}
              >
                {params.row.name}
              </Link>
            </Typography>

            <Typography variant="body2" sx={{ fontWeight: 'bold', color: yellow[300] }}>
              ITS: <span style={{ fontWeight: 'normal', color: brown[700] }}> {params.row.its}</span>
            </Typography>

            <Typography variant="body2" sx={{ fontWeight: 'bold', color: yellow[300] }}>
              Mobile: <span style={{ fontWeight: 'normal', color: brown[700] }}> {params.row.mobile}</span>
            </Typography>

            <Typography variant="body2" sx={{ fontWeight: 'bold', color: yellow[300] }}>
              Folio No: <span style={{ fontWeight: 'normal', color: brown[700] }}> {params.row.folio_no}</span>
            </Typography>

            <Typography variant="body2" sx={{ fontWeight: 'bold', color: yellow[300] }}>
              Sector:{' '}
              <span style={{ fontWeight: 'normal', color: brown[700] }}>
                {params.row.sector_name}-{params.row.sub_sector_name}
              </span>
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'receipt_no',
      headerName: 'Receipt No',
      width: 170,
      sortable: true,
      renderCell: (params) => {
        const raw = String(params.row.status || '').toLowerCase();
        const isCancelled = raw === 'cancelled' || raw === 'canceled';

        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                textAlign: 'center',
                color: brown[700],
                textDecoration: isCancelled ? 'line-through' : 'none',
                opacity: isCancelled ? 0.7 : 1,
                fontWeight: 600,
              }}
              title={isCancelled ? 'This receipt is cancelled' : ''}
            >
              {params.row.receipt_no}
            </Typography>

            {params.row.status && (
              <Typography
                variant="caption"
                sx={{ mt: 0.5, color: isCancelled ? '#d32f2f' : brown[700], fontWeight: 700, letterSpacing: 0.3 }}
              >
                {toUiStatus(params.row.status)}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'receipt',
      headerName: 'Receipt Details',
      width: 270,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', paddingTop: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: yellow[300] }}>
              Mode: <span style={{ fontWeight: 'normal', color: brown[700] }}> {params.row.mode}</span>
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: yellow[300] }}>
              Date: <span style={{ fontWeight: 'normal', color: brown[700] }}> {params.row.date}</span>
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: yellow[300] }}>
              Year: <span style={{ fontWeight: 'normal', color: brown[700] }}> {params.row.year}</span>
            </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: yellow[300] }}>
              Created by: <span style={{ fontWeight: 'normal', color: brown[700] }}> {params.row.log_user_name}</span>
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: yellow[300] }}>
              Comments: <span style={{ fontWeight: 'normal', color: brown[700] }}> {params.row.comments}</span>
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 180,
      sortable: true,
      renderCell: (params) => {
        const formattedAmount = new Intl.NumberFormat('en-IN', {
          style: 'currency',
          currency: 'INR',
        }).format(params.row.amount);

        return (
          <Typography
            variant="body2"
            sx={{
              color: '#388e3c',
              textAlign: 'right',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              height: '100%',
              fontSize: '18px',
              fontWeight: 'bold',
            }}
          >
            {formattedAmount}
          </Typography>
        );
      },
    },
    {
      field: 'action',
      headerName: 'Action',
      width: 170,
      sortable: false,
      renderCell: (params) => (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%',
          }}
        >
          <ActionButtonWithOptions
            row={params.row}
            onEdit={(row) => {
              setEditReceipt(row);
              setEditOpen(true);
            }}
          />
        </Box>
      ),
    },
  ];

  // Fetch data
  useEffect(() => {
    if (loading || !token || !selectedYear?.length) return;

    const fetchData = async () => {
      try {
        setLoadingData(true);

        const response = await fetch('https://api.fmb52.com/api/receipts/all', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ year: selectedYear, role_id: accessRoleId }),
        });

        if (!response.ok) {
          throw new Error(`Error ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        setRows(data.data || []);
        const users = [...new Set(data.data.map(item => item.log_user_name))];
        setUserNames(users);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoadingData(false);
      }
    };

    fetchData();
  }, [token, loading, selectedYear, accessRoleId, reloadKey]);

  // Filtered rows
  const filteredRows = rows.filter((row) => {
    const t = filterText.toLowerCase();
    const matchesFilterText =
      row.name?.toLowerCase().includes(t) ||
      row.its?.toLowerCase().includes(t) ||
      row.mobile?.toLowerCase().includes(t) ||
      row.folio_no?.toLowerCase().includes(t) ||
      row.sector_name?.toLowerCase().includes(t) ||
      row.sub_sector_name?.toLowerCase().includes(t) ||
      row.hof_its?.toLowerCase().includes(t);

    const matchesMode = modeFilter === 'All' || row.mode?.toLowerCase() === modeFilter.toLowerCase();

    // NEW: status filtering using UI labels (Deposited/Pending)
    const uiStatus = toUiStatus(row.status).toLowerCase(); // '' if no status
    const matchesStatus =
      statusFilter === 'All' || (uiStatus && uiStatus === statusFilter.toLowerCase());

    const matchesSector =
      !selectedSectorName?.length ||
      selectedSectorName.map((s) => s.toLowerCase()).includes(row.sector_name?.toLowerCase());

       const matchesCreatedBy =
      createdByFilter === '' || row.log_user_name?.toLowerCase() === createdByFilter.toLowerCase();

    return matchesFilterText && matchesMode && matchesStatus && matchesSector && matchesCreatedBy;
  });

  // After successful save from dialog, update the grid row
  const handleReceiptSaved = (updated) => {
    setRows((prev) =>
      prev.map((r) =>
        (updated.id && r.id === updated.id) || (updated.hashed_id && r.hashed_id === updated.hashed_id)
          ? { ...r, ...updated }
          : r
      )
    );
  };

  console.log('Edit', editReceipt);

  return (
    <AppTheme>
      <CssBaseline />
      <div
        style={{
          filter: snackbarOpen ? 'blur(5px)' : 'none',
          transition: 'filter 0.3s ease',
          pointerEvents: snackbarOpen ? 'none' : 'auto',
          userSelect: snackbarOpen ? 'none' : 'auto',
          marginTop: '95px',
        }}
      >
        <Box sx={{ width: '100%', overflowX: 'auto', pr: 2, pb: 3, pl: 2 }}>
          <Paper
            sx={{
              width: '100%',
              boxShadow: 1,
              overflowX: 'auto',
              p: 1,
              '@media (max-width: 600px)': { p: 1 },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 2,
              }}
            >
              <TextField
                label="Search"
                variant="outlined"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                sx={{ width: { xs: '100%', sm: '300px' } }}
                InputProps={{
                  sx: { height: '52px', display: 'flex', alignItems: 'center', mb: '7px' },
                }}
              />

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>

                <FormControl sx={{ minWidth: 190, width: { xs: '100%', sm: '170px' } }}>
                  <InputLabel>Filter By Created By</InputLabel>
                  <Select
                    label="Filter By Created By"
                    value={createdByFilter}
                    onChange={(e) => setCreatedByFilter(e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    {userNames.map((user) => (
                      <MenuItem key={user} value={user}>
                        {user}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* NEW: Status filter (left of Mode) */}
                <FormControl sx={{ minWidth: 170, width: { xs: '100%', sm: '170px' } }}>
                  <InputLabel>Filter By Status</InputLabel>
                  <Select
                    label="Filter By Status"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="Deposited">Deposited</MenuItem>
                    <MenuItem value="Pending">Pending</MenuItem>
                  </Select>
                </FormControl>

                <FormControl sx={{ minWidth: 150, width: { xs: '100%', sm: '150px' } }}>
                  <InputLabel>Filter By Mode</InputLabel>
                  <Select label="Filter By Mode" value={modeFilter} onChange={(e) => setModeFilter(e.target.value)}>
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="Cash">Cash</MenuItem>
                    <MenuItem value="Cheque">Cheque</MenuItem>
                    <MenuItem value="NEFT">NEFT</MenuItem>
                  </Select>
                </FormControl>

                <Button variant="contained" color="primary" startIcon={<DownloadIcon />} onClick={exportToExcel}>
                  Export to Excel
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<CloudUploadIcon />}
                  onClick={() => setReconcileOpen(true)}
                >
                  Reconcile Razorpay
                </Button>
              </Box>
            </Box>

            <div style={{ height: 700, width: '100%', overflow: 'auto' }}>
              <DataGridPro
                rows={filteredRows}
                columns={columns}
                components={{ Toolbar: GridToolbar }}
                localeText={customLocaleText}
                rowHeight={110}
                checkboxSelection
                disableSelectionOnClick
                pagination
                paginationMode="client"
                paginationModel={paginationModel}
                onPaginationModelChange={(model) => setPaginationModel(model)}
                pageSizeOptions={[5, 10, 25, 50, 100]}
                sortModel={sortModel}
                onSortModelChange={(model) => setSortModel(model)}
                getRowId={(row) => row.id}
                sx={{
                  '& .MuiDataGrid-columnHeaders': {
                    color: yellow[400],
                    textAlign: 'center',
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                  },
                  '& .MuiDataGrid-columnHeader:focus, .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
                  '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 'bold', textAlign: 'center' },
                  '& .MuiDataGrid-cell:hover': { backgroundColor: yellow[200] },
                  '& .MuiDataGrid-row:hover': { backgroundColor: yellow[100] },
                  '@media (max-width: 600px)': {
                    '& .MuiDataGrid-columnHeaders': { fontSize: '0.75rem' },
                    '& .MuiDataGrid-cell': { fontSize: '0.75rem' },
                  },
                }}
              />
            </div>
          </Paper>
        </Box>

        <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loadingData}>
          <CircularProgress color="inherit" />
        </Backdrop>
      </div>

      {/* Global snackbar */}
      <Snackbar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        sx={{ height: '100%' }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%' }}
          action={
            <Button color="inherit" size="small" onClick={() => setSnackbarOpen(false)}>
              OK
            </Button>
          }
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Edit dialog */}
      <EditReceiptDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        receipt={editReceipt}
        token={token}
        onSaved={handleReceiptSaved}
        setSnackbarOpen={setSnackbarOpen}
        setSnackbarMessage={setSnackbarMessage}
        setSnackbarSeverity={setSnackbarSeverity}
      />
      <RazorpayReconcileDialog
        open={reconcileOpen}
        onClose={() => setReconcileOpen(false)}
        token={token}
        onCreated={(json) => {
          setReloadKey((k) => k + 1);
          if (json?.message) {
            setSnackbarMessage(json.message);
            setSnackbarSeverity(json.errors?.length ? 'warning' : 'success');
            setSnackbarOpen(true);
          }
        }}
      />
    </AppTheme>
  );
}

export default Receipts;
